import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, CheckCircle2, Clock, MessageSquare, Send, RefreshCw, ArrowLeft, User, Phone, Mail, Filter, Search, Loader2 } from 'lucide-react';
import { concernsAPI } from '../../services/api';

export function AdminConcernsDashboard({ user }) {
  const navigate = useNavigate();
  const [concerns, setConcerns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyStatus, setReplyStatus] = useState('Resolved');
  const [isSending, setIsSending] = useState(false);

  const loadConcerns = async () => {
    setIsLoading(true);
    try {
      const res = await concernsAPI.getAllConcerns();
      if (res.success) {
        setConcerns(res.concerns || []);
      }
    } catch (err) {
      // ignore
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadConcerns();
  }, []);

  const handleSendReply = async (concernId) => {
    if (!replyText.trim()) {
      alert('Please enter a reply message.');
      return;
    }

    setIsSending(true);
    try {
      const res = await concernsAPI.replyConcern({
        concernId,
        status: replyStatus,
        replyMessage: replyText.trim(),
        adminId: user?.id
      });

      if (res.success) {
        setReplyingId(null);
        setReplyText('');
        loadConcerns();
      } else {
        alert('Failed to send reply.');
      }
    } catch (err) {
      alert(err.message || 'Error sending reply.');
    } finally {
      setIsSending(false);
    }
  };

  const filtered = concerns.filter(c => {
    const normStatus = (c.status || '').toLowerCase().replace('_', ' ');
    if (filterStatus !== 'all' && normStatus !== filterStatus.toLowerCase()) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const name = (c.userName || '').toLowerCase();
      const subj = (c.subject || '').toLowerCase();
      const cat = (c.category || '').toLowerCase();
      if (!name.includes(q) && !subj.includes(q) && !cat.includes(q)) return false;
    }
    return true;
  });

  const counts = {
    all: concerns.length,
    pending: concerns.filter(c => (c.status || '').toLowerCase() === 'pending').length,
    inProgress: concerns.filter(c => ['in progress', 'in_progress'].includes((c.status || '').toLowerCase())).length,
    resolved: concerns.filter(c => (c.status || '').toLowerCase() === 'resolved').length
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-emerald-50 p-4 md:p-6">
      <div className="max-w-5xl mx-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/profile')}
              className="text-gray-500 hover:text-gray-800 transition p-1.5 rounded-lg hover:bg-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">User Concerns & Feedback Management</h1>
              <p className="text-xs text-gray-500 mt-0.5">Review, manage, and respond to user and partner support tickets</p>
            </div>
          </div>
          <button
            onClick={loadConcerns}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-xs font-semibold rounded-xl hover:bg-gray-50 shadow-sm transition"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {/* Stats Banner */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { label: 'Pending', value: counts.pending, color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
            { label: 'In Progress', value: counts.inProgress, color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
            { label: 'Resolved', value: counts.resolved, color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
          ].map(s => (
            <div key={s.label} className={`rounded-2xl border p-4 text-center ${s.bg}`}>
              <p className={`text-2xl font-extrabold ${s.color}`}>{s.value}</p>
              <p className={`text-xs font-semibold ${s.color} opacity-80 mt-0.5`}>{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-4 flex flex-col sm:flex-row gap-3 items-center justify-between">
          <div className="flex gap-1.5 flex-wrap">
            {['all', 'pending', 'in progress', 'resolved'].map(st => (
              <button
                key={st}
                onClick={() => setFilterStatus(st)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold capitalize transition ${
                  filterStatus === st ? 'bg-amber-700 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {st}
              </button>
            ))}
          </div>

          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search concern subject or user..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-xl text-xs focus:ring-2 focus:ring-amber-500 outline-none"
            />
          </div>
        </div>

        {/* Tickets List */}
        {isLoading ? (
          <div className="text-center py-20 text-gray-500">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-amber-600" />
            <p className="text-xs">Loading support tickets...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center text-gray-400 border border-gray-100">
            <ShieldAlert className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p className="text-sm font-bold text-gray-600">No support tickets found</p>
            <p className="text-xs text-gray-400 mt-1">There are no concerns matching your current filter.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map(c => (
              <div key={c.id} className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-5 overflow-hidden">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="bg-amber-100 text-amber-900 text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase">
                        {c.category}
                      </span>
                      <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full capitalize ${
                        (c.status || '').toLowerCase() === 'resolved' 
                          ? 'bg-emerald-100 text-emerald-800' 
                          : ['in progress', 'in_progress'].includes((c.status || '').toLowerCase())
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        {(c.status || 'pending').replace('_', ' ')}
                      </span>
                    </div>
                    <h3 className="text-base font-bold text-gray-900 mt-1.5">{c.subject}</h3>
                  </div>
                  <span className="text-xs text-gray-400">
                    {c.submittedAt ? new Date(c.submittedAt).toLocaleString('en-PH') : ''}
                  </span>
                </div>

                <div className="bg-gray-50/70 p-3 rounded-xl text-xs space-y-1 text-gray-600 mb-3 border border-gray-100">
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="flex items-center gap-1 font-semibold text-gray-900"><User className="w-3.5 h-3.5 text-amber-600" />{c.userName} ({c.userRole})</span>
                    {c.contactNumber && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-gray-400" />{c.contactNumber}</span>}
                    {c.userEmail && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-gray-400" />{c.userEmail}</span>}
                  </div>
                  <p className="text-gray-800 text-xs mt-2 leading-relaxed bg-white p-3 rounded-lg border border-gray-100">
                    {c.details}
                  </p>
                </div>

                {/* Admin Reply Section */}
                {c.adminReply ? (
                  <div className="bg-emerald-50/80 border border-emerald-100 p-3 rounded-xl text-xs space-y-1">
                    <p className="font-bold text-emerald-900 flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Admin Response ({c.repliedAt ? new Date(c.repliedAt).toLocaleDateString() : ''}):
                    </p>
                    <p className="text-emerald-800 font-medium">{c.adminReply}</p>
                  </div>
                ) : (
                  replyingId === c.id ? (
                    <div className="mt-3 bg-amber-50/50 p-4 rounded-xl border border-amber-200 space-y-3 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-amber-900">Reply to {c.userName}:</span>
                        <select
                          value={replyStatus}
                          onChange={e => setReplyStatus(e.target.value)}
                          className="px-2.5 py-1 border border-gray-200 rounded-lg text-xs font-semibold bg-white"
                        >
                          <option value="Resolved">Mark as Resolved</option>
                          <option value="In Progress">Mark as In Progress</option>
                        </select>
                      </div>
                      <textarea
                        rows={3}
                        placeholder="Type your official administrative reply..."
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        className="w-full p-3 border border-gray-200 rounded-xl text-xs focus:ring-2 focus:ring-amber-500 outline-none resize-none"
                      />
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => { setReplyingId(null); setReplyText(''); }}
                          className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-gray-700 font-semibold rounded-xl"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleSendReply(c.id)}
                          disabled={isSending}
                          className="px-4 py-2 bg-amber-700 hover:bg-amber-800 text-white font-bold rounded-xl shadow flex items-center gap-1.5 disabled:opacity-50"
                        >
                          {isSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                          Send Reply
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setReplyingId(c.id); setReplyText(''); }}
                      className="mt-2 text-xs text-amber-700 hover:text-amber-900 font-bold flex items-center gap-1 hover:underline"
                    >
                      <MessageSquare className="w-3.5 h-3.5" /> Reply & Resolve Concern
                    </button>
                  )
                )}

              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
